import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../api'
import { LoadingRows, ErrorState, Empty, NoResults } from '../components/States'
import { useAsync, useDebounced } from '../lib/useAsync'
import { money } from '../lib/money'
import { statusWords } from '../lib/not-priced'
import { count as fmt } from '../lib/format'
import { PageNumbers } from '../components/PageNumbers'
import type { BenchmarkOverview, BenchmarkRow, RunningNow } from '../types'

/**
 * Twenty roles a page.
 *
 * Smaller than the library's hundred, and smaller than Market Data's forty,
 * because a row here is tall: a title, a line of family, level and location
 * under it, a status and a button. A hundred of those is about six thousand
 * pixels of scrolling before the pager comes into view, which is a list
 * nobody reaches the bottom of.
 */
const PAGE_SIZE = 20

/**
 * What is benchmarked, what is not, and the button that changes that.
 *
 * Three things on this screen are deliberate and easy to undo by accident.
 *
 * **The button names the number it will run**, because "Benchmark 94 job
 * descriptions" tells somebody whether this is a coffee or an afternoon and
 * "Run benchmarking" does not.
 *
 * **No match found is not an error.** It is a legitimate answer and is not
 * coloured like a failure.
 *
 * **Nothing here shows what a run costs us.** Not an estimate, not a total, not
 * per item, and not in an error message. That figure is our cost base, not the
 * customer's price.
 *
 * **A run going in the background says so, here.** A run outlives the page it
 * was started from: somebody starts one, closes the tab, comes back, and this
 * is the screen they come back to. It used to offer a button to start the
 * thing that was already running. Pressing it joined the run rather than
 * starting a second, which is correct and completely invisible.
 */
export function Benchmarking() {
  const navigate = useNavigate()
  /*
   * The search lives in the address, the page does not.
   *
   * Exactly what the library does. Coming back to a search you typed is worth
   * keeping; coming back to page four of it is not worth the address it would
   * cost, and the library settled that already.
   */
  const [params, setParams] = useSearchParams()
  const query = params.get('q') ?? ''
  const debounced = useDebounced(query)

  /*
   * Which page, as a number, because this screen can ask for one directly.
   *
   * No trail of cursors to keep. The server reads the whole library to answer
   * the counts, so any page is one slice of something already in hand, and it
   * knows exactly how many pages there are.
   */
  const [at, setAt] = useState(0)

  const { data, error, loading, reload } = useAsync<BenchmarkOverview>(
    () => api.benchmarkOverview({ q: debounced, page: at, limit: PAGE_SIZE }),
    [debounced, at],
  )

  // A new search is a new list, so it starts at the top rather than at
  // whatever page the old one happened to be on.
  useEffect(() => { setAt(0) }, [debounced])
  /*
   * There is no scope to choose, and a control offering one is not coming back.
   *
   * **A run covers what needs matching, and that is all it has ever covered.**
   * `scopeOf` takes a scope and discards it: a role whose own words, market
   * data and matching rules are all unchanged is not asked again, because the
   * model cannot be made to repeat itself and re-asking moves a pay band with
   * nothing behind it.
   *
   * A checkbox promising to run every role again outlived that rule. Ticking
   * it changed nothing, said nothing, and cost somebody several minutes
   * working out whether it was broken. Forcing one role is still possible,
   * from that role's own row.
   */
  const scope = 'changed' as const
  const [estimate, setEstimate] = useState<{ count: number; seconds: number } | null>(null)
  const [starting, setStarting] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)
  const [opening, setOpening] = useState<string | null>(null)
  /*
   * The run going right now, kept here so it can move without a reload.
   *
   * Seeded from the overview, then followed by polling. Undefined means the
   * overview has not answered yet; null means it answered that nothing is
   * going.
   */
  const [running, setRunning] = useState<RunningNow | null | undefined>(undefined)
  const watching = useRef(true)

  useEffect(() => { setRunning(data?.running ?? null) }, [data])

  /*
   * Following a run from this screen, on the cheap request rather than this one.
   *
   * The overview reads every role and every benchmark the company holds.
   * Asking for it every few seconds for the length of a run that can take
   * hours is a whole-library read a few seconds apart, to move one number.
   * The run record is a single row, which is why checking on it is allowed to
   * be frequent at all.
   *
   * When it finishes, the overview is asked for once. That is the only moment
   * the counts and the rows actually change.
   */
  useEffect(() => {
    if (!running || (running.state !== 'running' && running.state !== 'stopping')) return
    watching.current = true
    let timer: ReturnType<typeof setTimeout>
    const runId = running.runId

    const look = async () => {
      try {
        const out = await api.benchmarkRun(runId)
        if (!watching.current) return
        if (out.state === 'running' || out.state === 'stopping') {
          setRunning({
            runId, state: out.state, done: out.done, total: out.total,
            secondsLeft: out.secondsLeft, startedBy: out.startedBy, startedAt: out.startedAt,
          })
          timer = setTimeout(look, 4000)
        } else {
          // Over. Everything on this screen is now out of date at once.
          setRunning(null)
          reload()
        }
      } catch {
        /*
         * Quietly stop following rather than showing an error.
         *
         * The run is not this screen's job, and one failed check says nothing
         * about whether it is still going. An error here would put a red
         * message over a library that is perfectly readable.
         */
      }
    }
    timer = setTimeout(look, 4000)
    return () => { watching.current = false; clearTimeout(timer) }
  }, [running?.runId, running?.state])

  useEffect(() => {
    if (!data?.hasSurvey) return
    let live = true
    api.benchmarkEstimate(scope)
      .then((out) => { if (live) setEstimate({ count: out.count, seconds: out.seconds }) })
      .catch(() => { if (live) setEstimate(null) })
    return () => { live = false }
    /*
     * Tied to the state of the library, not to the response.
     *
     * It used to depend on the whole response, which was harmless while the
     * response was the whole library and changed only when the library did.
     * Now the response is one page, so that would ask again on every page
     * turn to get back a number that cannot have moved. The counts are the
     * thing the estimate actually follows: they change when a run finishes or
     * a role is edited, which is exactly when it is stale.
     */
  }, [data?.hasSurvey, scope, JSON.stringify(data?.counts)])

  if (loading) return <LoadingRows rows={8} columns={5} />
  if (error) return <ErrorState message="We could not load your benchmarks." onRetry={reload} />
  if (!data) return null

  // A customer who has bought nothing yet is at the beginning, not broken.
  // The item stays in the navigation so they can find out what this does.
  if (!data.hasSurvey) {
    return (
      <Empty
        title="Benchmarking needs market data first"
        action={{ label: 'Add a survey', onClick: () => navigate('/market-data/add') }}
      >
        <p>
          A benchmark compares your job descriptions against the salary survey your
          company has licensed. Add a survey and every role can be matched against it.
        </p>
      </Empty>
    )
  }

  const rows = data.items
  const total = data.total ?? rows.length
  const matched = data.matched ?? total
  const pages = data.pages ?? 1
  const here = data.page ?? 0
  const from = here * PAGE_SIZE + 1
  const to = from + rows.length - 1

  /*
   * The way in for one role.
   *
   * A role already looked at goes straight to its shortlist, because the
   * expensive part is done and ticking is arithmetic. One never looked at
   * needs the model first, so it starts a run for that role alone and the run
   * screen hands over when it finishes.
   */
  async function open(row: BenchmarkRow) {
    if (row.status !== 'never run') {
      navigate(`/benchmarking/${row.jdId}`)
      return
    }
    setOpening(row.jdId)
    setStartError(null)
    try {
      const out = await api.benchmarkOneRole(row.jdId)
      navigate(`/benchmarking/runs/${out.runId}?then=${encodeURIComponent(row.jdId)}`)
    } catch (err) {
      setStartError(err instanceof Error ? err.message : 'That did not start.')
      setOpening(null)
    }
  }

  async function start() {
    setStarting(true)
    setStartError(null)
    try {
      const out = await api.startBenchmarkRun(scope)
      navigate(`/benchmarking/runs/${out.runId}`)
    } catch (err) {
      setStartError(err instanceof Error ? err.message : 'That did not start.')
      setStarting(false)
    }
  }

  return (
    <section className="screen">
      {/*
        * The same header every other module has. This screen had none at all,
        * so it opened on a row of counters with nothing saying what it was.
        */}
      <header className="title-row">
        <div>
          <h1 className="page">Benchmarking</h1>
          <p className="subline">
            Pick a role, confirm the market matches, and save the band.
          </p>
        </div>
      </header>

      {/*
        Two numbers, and they add up to the library.

        It used to be five, four of which were states of the matching engine
        rather than anything somebody came to the screen to ask. Reading them
        meant adding three together in your head to work out whether the button
        was worth pressing. The per-role rows still carry every state, so what
        went is the arithmetic, not the detail.
      */}
      <div className="counts" role="group" aria-label="Where your roles stand">
        <Count label="Benchmarked" value={data.counts.done ?? 0} />
        <Count label="Still to benchmark" value={data.counts.toDo ?? 0} />
      </div>

      {/*
        * One card, saying one of two things.
        *
        * Either there is a run going or there is not, and the same place on
        * the screen answers that. A progress banner above a start button is
        * two controls for one question, and the second of them is a button
        * somebody can press that does not do what it says.
        */}
      {running
        ? <RunningCard run={running} onWatch={() => navigate(`/benchmarking/runs/${running.runId}`)} />
        : (
          <section className="card card--pad start">
            <div className="start__main">
              <button type="button" className="btn btn--primary" onClick={start} disabled={starting || estimate?.count === 0}>
                {estimate === null
                  ? 'Benchmark what has changed'
                  : `Benchmark ${estimate.count.toLocaleString()} job description${estimate.count === 1 ? '' : 's'}`}
              </button>
              {estimate !== null && estimate.count > 0 && (
                // How long, never how much. Enough to know whether to wait.
                <p className="subline">About {howLong(estimate.seconds)}. You can close the page once it starts.</p>
              )}
              {estimate?.count === 0 && (
                <p className="subline">Everything is benchmarked against its current version.</p>
              )}
            </div>
            {startError && <p className="field__error" role="alert">{startError}</p>}
          </section>
        )}

      <div className="toolbar">
        <label className="field field--search">
          <span className="sr-only">Search your roles</span>
          <input
            type="search"
            value={query}
            placeholder="Search by title, family, level or anything in the role"
            onChange={(e) => {
              const next = new URLSearchParams(params)
              if (e.target.value) next.set('q', e.target.value)
              else next.delete('q')
              setParams(next, { replace: true })
            }}
          />
        </label>
      </div>

      {rows.length === 0 && query.trim() !== '' ? (
        <NoResults query={query} onClear={() => { const n = new URLSearchParams(params); n.delete('q'); setParams(n, { replace: true }) }} />
      ) : (
        /*
         * A list of roles, not a spreadsheet of them.
         *
         * This was a five column table. A table is right when the columns are
         * compared down the page, and these are not: family, level and
         * location describe one role and are read together. So each row reads
         * as the role, then what it pays, then where it sits, then the way in.
         */
        <section className="card card--flush">
          <h2 className="card__head">Select a job description</h2>
          <ul className="picker">
            {rows.map((row) => (
              <li className="picker__row" key={row.jdId}>
                <div className="picker__what">
                  <a
                    className="picker__title"
                    href={`/benchmarking/${row.jdId}`}
                    onClick={(e) => { e.preventDefault(); navigate(`/benchmarking/${row.jdId}`) }}
                  >
                    {row.title}
                  </a>
                  <div className="picker__meta">
                    {[row.family, row.level, row.location].filter(Boolean).join(' \u00b7 ')}
                  </div>
                </div>
                {/*
                  * The market median, not the role's own range. Most job
                  * descriptions here name no pay, so that column read "No pay
                  * range" most of the way down while the answer a person came
                  * for was a click away.
                  */}
                <div className="picker__pay num">
                  {row.median == null ? '' : money(row.median, row.currency ?? null)}
                </div>
                {/*
                  * No compa-ratio column. It is a role's own midpoint over the
                  * market median, and job descriptions here carry no pay
                  * range, so it was an empty column on every row.
                  */}
                <div className="picker__status">
                  <StatusWord status={row.status} confirmed={row.confirmed} />
                </div>
                {/*
                  Named for what it will do to this row, not for the screen
                  it sits on.

                  **It read "Benchmark" on every row and benchmarked almost
                  none of them.** The same button starts a run for a role
                  never looked at and opens the detail screen for a role that
                  already has an answer, and on a library that has been run
                  that is every row. One word covering two actions is worse
                  than a word that is simply wrong, because it is right often
                  enough to be trusted.

                  "Review" rather than "View" or "Open" because the screen it
                  opens is where the match is confirmed and the band is saved,
                  which asks something of the reader. Out of date, no match
                  found and could not be matched all say Review too: each of
                  them has something worth reading.
                */}
                <button
                  type="button"
                  className="btn"
                  disabled={opening === row.jdId}
                  onClick={() => open(row)}
                >
                  {opening === row.jdId
                    ? 'Finding matches'
                    : row.status === 'never run' ? 'Benchmark' : 'Review'}
                </button>
              </li>
            ))}
          </ul>
          {/*
            * Only when there is more than one page.
            *
            * A pager under a list of nine roles is a row of dead buttons and
            * a line saying "1 to 9 of 9", which is furniture rather than
            * information.
            */}
          {pages > 1 && (
            <nav className="pager" aria-label="Benchmarking pages">
              <p className="subline">
                Page {fmt(here + 1)} of {fmt(pages)} · showing {fmt(from)} to {fmt(to)} of{' '}
                {fmt(matched)}{query.trim() ? ' matching' : ''}
              </p>
              {/*
                * `open` is false and the last number is always the last page.
                * Market Data cannot always know its total and marks the end as
                * uncertain; here every number offered is a page that exists.
                */}
              <PageNumbers
                at={here}
                pages={pages}
                open={false}
                busy={loading}
                onGo={(n) => setAt(Math.max(0, Math.min(n, pages - 1)))}
              />
            </nav>
          )}
        </section>
      )}
    </section>
  )
}

/**
 * A run happening somewhere else, said on the screen you came back to.
 *
 * **Determinate, not a spinner.** A spinner on something that takes an hour
 * tells a person nothing except that it has not crashed. A count, a bar and a
 * rough time left tell them whether to wait, and that is the decision they
 * are actually making.
 *
 * **It does not block anything.** The library below stays readable and every
 * role on it stays openable while this sits at the top. A run is background
 * work and a screen that waits for it has turned background work back into
 * foreground work.
 *
 * **Announced as well as drawn**, so somebody who is not watching the bar is
 * still told it finished.
 *
 * No cost, no token count, no price. Time left only.
 */
function RunningCard({ run, onWatch }: { run: RunningNow; onWatch: () => void }) {
  const percent = run.total === 0 ? 0 : Math.round((run.done / run.total) * 100)
  const stopping = run.state === 'stopping'
  return (
    <section className="card card--pad running" aria-labelledby="running-head">
      <div className="running__head">
        <div>
          <h2 id="running-head" className="running__title">
            {/* A live dot, and the word as well. Colour alone is not a state. */}
            <span className="running__dot" aria-hidden="true" />
            {stopping ? 'Stopping' : 'Benchmarking is running'}
          </h2>
          <p className="subline">
            Started by {run.startedBy}, {new Date(run.startedAt).toLocaleString()}.
          </p>
        </div>
        <button type="button" className="btn" onClick={onWatch}>Watch it</button>
      </div>

      <div
        className="progress"
        role="progressbar"
        aria-valuenow={run.done}
        aria-valuemin={0}
        aria-valuemax={run.total}
        aria-label={`${run.done} of ${run.total} job descriptions benchmarked`}
      >
        <div className="progress__bar" style={{ width: `${percent}%` }} />
      </div>

      <p className="subline" aria-live="polite">
        <b>{run.done.toLocaleString()} of {run.total.toLocaleString()} done.</b>{' '}
        {stopping
          ? 'The roles already started will finish. Everything done is saved.'
          : `About ${howLong(run.secondsLeft)} left. You can leave this page. The run carries on and finishes on its own.`}
      </p>
    </section>
  )
}

const Count = ({ label, value }: { label: string; value: number }) => (
  <div className="count">
    <div className="count__value">{value.toLocaleString()}</div>
    <div className="count__label">{label}</div>
  </div>
)

/**
 * Where a role stands, as a word as well as a colour.
 *
 * **Not where it sits against the market.** That is a role's own midpoint
 * against the median, and these job descriptions carry no pay range, so the
 * column read "no pay range set" most of the way down and said nothing about
 * whether the benchmark was any good. It says whether there is an answer now,
 * and the answer itself is the median beside it.
 *
 * No match found gets the neutral class, not the failure one: it is an answer,
 * and colouring it red tells somebody their data is broken when it is not.
 * Only a role that actually broke is coloured as a problem.
 *
 * The market position words are still handled, because a benchmark saved
 * before they were dropped still carries one and should render rather than
 * fall through to nothing.
 */
function StatusWord({ status, confirmed }: {
  status: BenchmarkRow['status']
  confirmed?: boolean
}) {
  const tone =
    status === 'benchmarked' || status === 'above market'
      || status === 'at market' || status === 'below market' ? 'pill--at'
      : status === 'out of date' ? 'pill--stale'
        : status === 'could not be matched' ? 'pill--below'
          : 'pill--neutral'
  return (
    <span className={`pill ${tone}`}>
      {/* A role that was not priced says why, never that nothing was found (STE-1063). */}
      {statusWords(status)}
      {/* A band somebody confirmed is a different thing to defend. */}
      {confirmed && status === 'benchmarked' ? ', confirmed' : ''}
    </span>
  )
}

/**
 * Plain words. "About 25 minutes" reads; "1500 seconds" does not.
 *
 * **Every caller puts "About" in front of this**, so nothing it returns may
 * begin with its own hedge. It used to answer "less than two minutes" for a
 * short run, which came out on screen as "About less than two minutes left".
 * Read in a browser, not caught by a type.
 */
export function howLong(seconds: number): string {
  if (seconds < 90) return 'a minute or two'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} minutes`
  const hours = Math.round(minutes / 30) / 2
  return `${hours} hour${hours === 1 ? '' : 's'}`
}
